import { novels, type NovelData } from "@/mocks/novels";
import { moreNovels } from "@/mocks/novelsMore";
import { categories } from "@/mocks/categories";

const catalog: NovelData[] = [...novels, ...moreNovels];

export interface Chapter {
  id: string;
  index: number;
  title: string;
  chapterId: string;
}

export interface Novel extends NovelData {
  chapters: Chapter[];
}

export function buildChapters(novel: NovelData): Chapter[] {
  return novel.chapterTitles.map((title, index) => ({
    id: `${novel.id}-c${index + 1}`,
    chapterId: `c${index + 1}`,
    index,
    title,
  }));
}

export function toNovel(novel: NovelData): Novel {
  return { ...novel, chapters: buildChapters(novel) };
}

export function getNovelById(id: string): Novel | undefined {
  const found = catalog.find((item) => item.id === id);
  return found ? toNovel(found) : undefined;
}

export function getNovelsByCategory(category: string): Novel[] {
  return catalog.filter((item) => item.category === category).map(toNovel);
}

export function getAllNovels(): Novel[] {
  return catalog.map(toNovel);
}

export function getCategory(slug: string) {
  return categories.find((item) => item.slug === slug);
}

export function getNovelChapter(novelId: string, chapterId: string) {
  const novel = getNovelById(novelId);
  if (!novel) return undefined;
  const index = novel.chapters.findIndex((chapter) => chapter.chapterId === chapterId);
  if (index < 0) return undefined;
  return { novel, chapter: novel.chapters[index], index };
}

export function getRelatedNovels(novel: Novel, limit = 6): Novel[] {
  const byCategory = catalog
    .filter((item) => item.id !== novel.id && item.category === novel.category)
    .map(toNovel);
  const byAuthor = catalog
    .filter((item) => item.id !== novel.id && item.author === novel.author)
    .map(toNovel);
  const others = catalog
    .filter((item) => item.id !== novel.id && item.category !== novel.category)
    .map(toNovel);

  const merged: Novel[] = [];
  const seen = new Set<string>();
  [...byCategory, ...byAuthor, ...others].forEach((item) => {
    if (!seen.has(item.id) && merged.length < limit) {
      seen.add(item.id);
      merged.push(item);
    }
  });
  return merged;
}

export function searchNovels(keyword: string): Novel[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return catalog
    .filter((item) => {
      return (
        item.title.toLowerCase().includes(q) ||
        item.author.toLowerCase().includes(q) ||
        item.categoryName.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q)) ||
        item.intro.toLowerCase().includes(q)
      );
    })
    .map(toNovel);
}

export function getHotRanking(limit = 8): Novel[] {
  return [...catalog]
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, limit)
    .map(toNovel);
}

export function getLatestUpdates(limit = 8): Novel[] {
  return [...catalog]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit)
    .map(toNovel);
}

export function formatWordCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万字`;
  }
  return `${count}字`;
}

export function formatCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return `${count}`;
}